import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

export type LiveEventType =
  | 'task.completed'
  | 'task.updated'
  | 'task.created'
  | 'issue.created'
  | 'issue.updated'
  | 'plan.changed'
  | 'staff.changed'

export interface LiveEvent {
  type: LiveEventType
  payload?: Record<string, unknown>
  at: string
}

/** Which cached queries each event invalidates. */
const AFFECTED: Record<LiveEventType, string[]> = {
  'task.completed': ['occurrences', 'dashboard', 'occurrence', 'staff-detail'],
  'task.updated': ['occurrences', 'dashboard', 'occurrence', 'staff-detail'],
  'task.created': ['occurrences', 'dashboard', 'staff-detail'],
  'issue.created': ['issues', 'dashboard'],
  'issue.updated': ['issues', 'dashboard', 'issue'],
  'plan.changed': ['plans', 'occurrences', 'dashboard', 'setup'],
  'staff.changed': ['staff', 'staff-detail', 'dashboard', 'setup'],
}

/**
 * Live updates from the server.
 *
 * The Verwalter keeps the dashboard open all morning; without this they would be
 * looking at whatever was true when the page loaded. Events carry no data the UI
 * trusts, they only say "this got stale", and the normal authorised request
 * fetches the actual content.
 */
export function useLiveUpdates(): { connected: boolean; lastEventAt: string | null } {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)
  const [lastEventAt, setLastEventAt] = useState<string | null>(null)

  useEffect(() => {
    if (typeof EventSource === 'undefined') return

    const source = new EventSource('/api/events')
    source.onopen = () => setConnected(true)
    source.onerror = () => setConnected(false)

    const handle = (event: MessageEvent) => {
      let parsed: LiveEvent
      try {
        parsed = JSON.parse(event.data) as LiveEvent
      } catch {
        return
      }
      // The greeting frame sent on connect carries no change.
      if (parsed.payload?.hello) return

      setLastEventAt(parsed.at)
      for (const key of AFFECTED[parsed.type] ?? []) {
        void queryClient.invalidateQueries({ queryKey: [key] })
      }
    }

    for (const type of Object.keys(AFFECTED) as LiveEventType[]) {
      source.addEventListener(type, handle as EventListener)
    }

    return () => {
      for (const type of Object.keys(AFFECTED) as LiveEventType[]) {
        source.removeEventListener(type, handle as EventListener)
      }
      source.close()
    }
  }, [queryClient])

  return { connected, lastEventAt }
}
